import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../utils/axios";
import CrosswordSelector from "../components/CrosswordSelector";
import "./SessionCreatorPage.css";
import { getUser } from "../utils/auth";
import logger from "../utils/logger";
import CrosswordGridClues from "../components/CrosswordGridClues";
import { CrosswordSessionDto } from "../models/crossword-session-dto";
import { MeetingType } from "../models/meeting-type";

interface NotificationRecipient {
  email: string;
  username: string;
}

const SessionCreatorPage = () => {
  const navigate = useNavigate();
  const [selectedCrosswordId, setSelectedCrosswordId] = useState<number | null>(null);
  const [sessionTitle, setSessionTitle] = useState(`${getUser()?.username} - ${new Date().toLocaleString()}`);
  const [allowJoin, setAllowJoin] = useState(true);
  const [meetingType, setMeetingType] = useState(MeetingType.Teams);
  const [creating, setCreating] = useState(false);
  const [excludeParticipated, setExcludeParticipated] = useState(true);
  const [notificationRecipients, setNotificationRecipients] = useState<NotificationRecipient[]>([]);
  const [selectedNotificationEmails, setSelectedNotificationEmails] = useState<Set<string>>(new Set());
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [recipientError, setRecipientError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedCrosswordId) return;

    const loadNotificationRecipients = async () => {
      setLoadingRecipients(true);
      setRecipientError(null);
      try {
        const response = await axios.get<NotificationRecipient[]>('/api/cross/session/notification-recipients');
        setNotificationRecipients(response.data);
        setSelectedNotificationEmails(new Set(response.data.map((recipient) => recipient.email)));
      } catch (error) {
        logger.error('Failed to load notification recipients:', error);
        setRecipientError('Unable to load notification recipients.');
      } finally {
        setLoadingRecipients(false);
      }
    };

    loadNotificationRecipients();
  }, [selectedCrosswordId]);

  const onCreateSession = async () => {
    if (!selectedCrosswordId) return;
    
    const user = getUser();
    if (!user) {
      alert('You must be logged in to create a session.');
      navigate('/login');
      return;
    }

    try {
      setCreating(true);
      const sessionDto: CrosswordSessionDto = {
        id: selectedCrosswordId,
        name: sessionTitle,
        isOpenDoor: allowJoin,
        isCompleted: false,
        users: {[user.username] : 'A' },
        startTime: new Date(),
        clueStatuses: [],
        crosswordId: selectedCrosswordId,
        gridState: "",
        scratchpad: "",
        excludedNotificationEmails: notificationRecipients
          .filter((recipient) => !selectedNotificationEmails.has(recipient.email))
          .map((recipient) => recipient.email),
        meetingUrl: "",
        meetingType,
      };

      const response = await axios.post<CrosswordSessionDto>('/api/cross/session', sessionDto);
      const sessionId = response.data;
      
      // Navigate to the session page
      navigate(`/session/${sessionId}`);
    } catch (error) {
      logger.error('Failed to create session:', error);
      alert('Failed to create session. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="main-content">
      {!selectedCrosswordId && (
        <>
          <label className="exclude-participated">
            <input
              type="checkbox"
              checked={excludeParticipated}
              onChange={(event) => setExcludeParticipated(event.target.checked)}
            />
            Exclude crosswords I have participated in
          </label>
          <CrosswordSelector
            onCrosswordSelected={setSelectedCrosswordId}
            excludeParticipated={excludeParticipated}
          />
        </>
      )}
      {selectedCrosswordId && (
        <div className="preview-container">
          <div className="session-creation">
            <h3>Create New Session</h3>
            <div className="session-form">
              <label>
                Session Title:
                <input
                  type="text"
                  value={sessionTitle}
                  onChange={(e) => setSessionTitle(e.target.value)}
                />
              </label>
              <label className="allow-join">
                <input
                  type="checkbox"
                  checked={allowJoin}
                  onChange={(e) => setAllowJoin(e.target.checked)}
                />
                Allow Others to Join
              </label>
              <label className="have-teams">
                Meeting type:
                <select
                  value={meetingType}
                  onChange={(event) => setMeetingType(Number(event.target.value) as MeetingType)}
                >
                  <option value={MeetingType.Teams}>Teams</option>
                  <option value={MeetingType.Jitsi}>Jitsi</option>
                  <option value={MeetingType.None}>None</option>
                </select>
              </label>
              <fieldset className="notification-recipients">
                <legend>Notify about this session</legend>
                {loadingRecipients && <p>Loading recipients...</p>}
                {recipientError && <p role="alert">{recipientError}</p>}
                {!loadingRecipients && !recipientError && notificationRecipients.length === 0 && (
                  <p>No one has opted in to new session notifications.</p>
                )}
                {notificationRecipients.map((recipient) => (
                  <label key={recipient.email} className="notification-recipient">
                    <input
                      type="checkbox"
                      checked={selectedNotificationEmails.has(recipient.email)}
                      disabled={creating}
                      onChange={(event) => {
                        setSelectedNotificationEmails((current) => {
                          const updated = new Set(current);
                          if (event.target.checked) updated.add(recipient.email);
                          else updated.delete(recipient.email);
                          return updated;
                        });
                      }}
                    />
                    <span>{recipient.username}</span>
                  </label>
                ))}
              </fieldset>

              <div className="button-group">
                <button 
                  className="create-session" 
                  onClick={onCreateSession}
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create Session'}
                </button>
                <button 
                  className="cancel-button" 
                  onClick={() => setSelectedCrosswordId(null)}
                  disabled={creating}
                >
                  Cancel (back to selection)
                </button>
              </div>
            </div>
          </div>
          <CrosswordGridClues crosswordId={selectedCrosswordId} readonly={true} sessionId={0}/>
          
        </div>
      )}
    </div>
  );
};

export default SessionCreatorPage;
